import React from "react";
import { Heading, Subheading } from "../../design/heading";
import { useEvent } from "../../contexts/EventContext";
import EmptyStateView from "../../components/EmptyStateView";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import EventListItem from "../EventListPage/components/EventList/EventListIItem";
import { useNavigate, useParams } from "react-router-dom";
import { useEventType } from "../../contexts/EventTypeContext";
import EventTypeBadge from "../../components/EventTypeBadge";
import { Text } from "../../design/text";
import Breadcrumbs from "../../components/Breadcrumbs";
import EventSubtypeButton from "../../components/EventSubtypeButton";
import EventButton from "../../components/EventButton";

const EventSubtypeDetailPage: React.FC = () => {
  const { eventSubtypeId } = useParams<{ eventSubtypeId: string }>();
  const { events } = useEvent();
  const { eventCategories, eventTypes, eventSubtypes } = useEventType();
  const navigate = useNavigate();

  const eventSubtype = eventSubtypes.find(
    (subtype) => subtype._id === eventSubtypeId,
  );
  const eventType = eventTypes.find(
    (type) => type._id === eventSubtype?.eventType,
  );
  const eventCategory = eventCategories.find(
    (category) => category._id === eventType?.eventCategory,
  );

  const filteredEvents = events.filter(
    (event) => event.eventSubtypeId === eventSubtypeId,
  );

  return (
    <div className="container p-4 mx-auto lg:p-8">
      <Breadcrumbs
        items={[
          { label: "Event Categories", href: "/event-categories" },
          {
            label: eventCategory?.name ?? "Event Category",
            href: `/event-category/${eventCategory?._id}`,
          },
          {
            label: eventType?.name ?? "Event Type",
            href: `/event-type/${eventType?._id}`,
          },
          {
            label: eventSubtype?.name ?? "Event Subtype",
            href: `/event-subtype/${eventSubtypeId}`,
          },
        ]}
      />

      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <Heading>{eventSubtype?.name}</Heading>
            <Text>{eventSubtype?.description}</Text>
            {eventSubtype && (
              <EventTypeBadge
                eventTypeId={eventSubtype.eventType}
                eventTypes={eventTypes}
                onClick={() =>
                  navigate(`/event-type/${eventSubtype.eventType}`)
                }
              />
            )}
          </div>
          <div className="flex gap-2">
            {eventSubtype && (
              <EventSubtypeButton
                eventTypeId={eventSubtype.eventType}
                eventSubtype={eventSubtype}
              />
            )}
          </div>
        </div>

        {filteredEvents.length === 0 && (
          <EmptyStateView
            title="No Events"
            message="No events found. Create an event to get started."
            action={<EventButton date={new Date()} />}
          />
        )}
        {filteredEvents.length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <Subheading>Events</Subheading>
              <EventButton date={new Date()} />
            </div>
            <Table className="mt-8 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
              <TableHead>
                <TableRow>
                  <TableHeader>Date</TableHeader>
                  <TableHeader>People Involved</TableHeader>
                  <TableHeader>Type</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEvents.map((event) => (
                  <EventListItem key={event._id} event={event} />
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </div>
    </div>
  );
};

export default EventSubtypeDetailPage;
